import type {
  CalendarEvent,
  CalendarEventAttendee,
  MeetingId,
} from './meetings.types';
import type { Meeting as StoreMeeting } from '@littlebird/sdk';

/**
 * Filter meeting IDs by a free-text query. Matches against title, attendee
 * display names / emails, summary, and tldr. Empty queries are pass-through.
 */
export function filterMeetingIdsBySearch(
  meetingIds: MeetingId[],
  query: string,
  meetingsStore: {
    get: (
      id: MeetingId
    ) =>
      | Pick<
          StoreMeeting,
          'name' | 'summary' | 'tldr' | 'attendees' | 'eventId'
        >
      | undefined;
  },
  calendarEvents: CalendarEvent[]
): MeetingId[] {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return meetingIds;

  const eventsById = new Map(calendarEvents.map((e) => [e.id, e]));

  return meetingIds.filter((id) => {
    const meeting = meetingsStore.get(id);
    if (!meeting) return false;

    const event = meeting.eventId ? eventsById.get(meeting.eventId) : null;
    const storeAttendees = (meeting.attendees ?? []).map(
      (a) => `${a.display_name ?? ''} ${a.email ?? ''}`
    );
    const eventAttendees = (event?.attendees ?? []).map(
      (a) => `${a.displayName ?? ''} ${a.email ?? ''}`
    );

    const haystack = [
      meeting.name ?? '',
      meeting.summary ?? '',
      meeting.tldr ?? '',
      ...storeAttendees,
      ...eventAttendees,
    ]
      .join(' ')
      .toLowerCase();

    return haystack.includes(trimmed);
  });
}

/**
 * Format attendees list for display.
 * Returns "Me" for empty list, "Name" for single, "A & B" for two,
 * or "A, B & N others" for 3+.
 */
export function formatAttendees(
  attendees: CalendarEventAttendee[] | null | undefined
): string {
  if (!attendees || attendees.length === 0) return 'Me';

  const getName = (attendee: CalendarEventAttendee) => {
    const name =
      attendee.displayName || attendee.email?.split('@')[0] || 'Unknown';
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  if (attendees.length === 1) return getName(attendees[0]);
  if (attendees.length === 2)
    return `${getName(attendees[0])} & ${getName(attendees[1])}`;

  const first = getName(attendees[0]);
  const second = getName(attendees[1]);
  const remaining = attendees.length - 2;
  return `${first}, ${second} & ${remaining} other${remaining === 1 ? '' : 's'}`;
}
